import { nextSprint, prevSprint } from '@nedelnik/shared';
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { haptic } from '../haptic.js';

import type { Period, SprintKey } from '@nedelnik/shared';

/**
 * Листание недель свайпом по свободному месту экрана (3.2.6).
 *
 * Страница едет за пальцем, как в Календаре iOS: палец влево — справа
 * въезжает следующая неделя, вправо — слева предыдущая. Так же стоят стрелки
 * в шапке: › справа — вперёд (решение владельца 2026-10-18).
 *
 * Соседняя неделя едет рядом, вплотную, как следующий снимок в Фото: под
 * пальцем видно, куда листаешь (отзыв 2026-10-24). Раньше старая неделя
 * уезжала в пустоту, и только потом въезжала новая — «переворачивание», как
 * при переключении окон. Соседа рисует `App` по `peek`, пока жест идёт; после
 * перелистывания он становится текущей неделей в том же кадре.
 *
 * Слева от текущей недели — Входящие, а не прошлая неделя: туда с текущей
 * ходят чаще, а прошлые недели остаются за стрелкой ‹. Из Входящих вправо —
 * в текущую, влево — некуда, страница пружинит.
 *
 * Едет весь экран: шапка, строка записи, теги, задачи и подвал (замечание
 * владельца 2026-10-18 — одна уезжающая область задач выглядела сломанной).
 * Снаружи только карточка и плашка отмены: они `position: fixed`, а
 * `transform` у предка привязал бы их к странице, а не к экрану.
 *
 * Жест не начинается там, где у пальца своя работа: на строках задач (их
 * свайп — перенос задачи), в ленте тегов (она листается вбок), в полях
 * ввода и раскрытых меню.
 */

export type PageStep = 'next' | 'back';

/**
 * Соседняя страница на время жеста. `left` — сдвиг от текущей на ширину
 * экрана, `top` — прокрутка страницы: сосед стоит верхом у верха экрана,
 * как встанет после перелистывания, даже если текущую неделю пролистали вниз.
 */
export interface PagePeek {
  to: Period;
  left: number;
  top: number;
}

/** Куда ведёт шаг: спринт, Входящие или некуда. */
export function pageTarget(period: Period, current: SprintKey, step: PageStep): Period | undefined {
  if (step === 'next') return period === 'inbox' ? current : nextSprint(period);
  if (period === 'inbox') return undefined;
  if (period === current) return 'inbox';
  return prevSprint(period);
}

/** Где жест не начинается. */
const OWN_GESTURES = '.row, .swipe, .tagbar, input, textarea, select, .menu__list, .overlay, .undo';

const LOCK_PX = 10;

/** Перелистнуть — четверть ширины, но не меньше 80 пикселей. */
function turnsAt(width: number): number {
  return Math.max(80, width * 0.25);
}

const TURN_MS = 180;

/** Клик, которым iOS закончит касание после жеста, гасится. */
const QUIET_MS = 400;

export function usePageSwipe(options: {
  enabled: boolean;
  target: (step: PageStep) => Period | undefined;
  onTurn: (to: Period) => void;
}): {
  shellRef: (element: HTMLElement | null) => void;
  pageRef: (element: HTMLElement | null) => void;
  peek: PagePeek | undefined;
} {
  const [shell, setShell] = useState<HTMLElement | null>(null);
  const [page, setPage] = useState<HTMLElement | null>(null);
  const [peek, setPeek] = useState<PagePeek | undefined>(undefined);
  const latest = useRef(options);
  latest.current = options;

  useEffect(() => {
    if (!options.enabled || shell === null || page === null) return undefined;
    const area: HTMLElement = shell;
    const moving: HTMLElement = page;

    let gesture:
      | {
          x0: number;
          y0: number;
          mode: 'pending' | 'swipe' | 'scroll';
          stride: number;
          dx: number;
          shown?: PageStep;
        }
      | undefined;
    let quietSince = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];

    function place(offset: number, animate: boolean): void {
      // В покое — ни сдвига, ни перехода: экран ничем не отличается от
      // экрана, который никто не листал.
      moving.style.transition = animate
        ? `transform ${String(TURN_MS)}ms ease-out`
        : offset === 0
          ? ''
          : 'none';
      moving.style.transform = offset === 0 ? '' : `translateX(${String(offset)}px)`;
      // Обрезка по ширине — только пока экран в движении (см. `.is-turning`
      // в стилях); в покое её нет вовсе.
      area.classList.toggle('is-turning', !(offset === 0 && !animate));
    }

    function onStart(event: TouchEvent): void {
      const touch = event.touches[0];
      gesture = undefined;
      if (event.touches.length !== 1 || touch === undefined) return;
      if (event.target instanceof Element && event.target.closest(OWN_GESTURES) !== null) return;
      gesture = { x0: touch.clientX, y0: touch.clientY, mode: 'pending', stride: 0, dx: 0 };
    }

    function onMove(event: TouchEvent): void {
      const touch = event.touches[0];
      if (gesture === undefined || gesture.mode === 'scroll' || touch === undefined) return;

      const dx = touch.clientX - gesture.x0;
      const dy = touch.clientY - gesture.y0;

      if (gesture.mode === 'pending') {
        if (Math.abs(dy) > LOCK_PX && Math.abs(dy) >= Math.abs(dx)) {
          gesture.mode = 'scroll';
          return;
        }
        if (Math.abs(dx) <= LOCK_PX) return;
        gesture.mode = 'swipe';
        // Шаг — ширина оболочки, а не страницы: у оболочки поля по бокам, и
        // сосед, сдвинутый на ширину страницы, стоял бы вплотную без них.
        gesture.stride = area.clientWidth;
      }

      event.preventDefault();
      gesture.dx = dx;
      const step: PageStep = dx < 0 ? 'next' : 'back';
      // Листать некуда — страница тянется четвертью хода и не дальше 40
      // пикселей, как строка без действия.
      const to = latest.current.target(step);
      if (to !== undefined && gesture.shown !== step) {
        gesture.shown = step;
        // Сдвиг соседа — в его же системе: `left` от левого края страницы,
        // а сама страница уже стоит на полях оболочки.
        setPeek({ to, left: step === 'next' ? gesture.stride : -gesture.stride, top: scrollY });
      }
      place(to !== undefined ? dx : Math.sign(dx) * Math.min(Math.abs(dx) * 0.25, 40), false);
    }

    /** Покой: ни сдвига, ни соседа. Сосед снимается в том же кадре, что и сдвиг. */
    function rest(): void {
      flushSync(() => setPeek(undefined));
      place(0, false);
    }

    function settleBack(): void {
      place(0, true);
      timers.push(setTimeout(rest, TURN_MS + 50));
    }

    function onEnd(): void {
      const ended = gesture;
      gesture = undefined;
      if (ended?.mode !== 'swipe') return;
      quietSince = Date.now();

      const step: PageStep = ended.dx < 0 ? 'next' : 'back';
      const to = latest.current.target(step);
      if (to === undefined || ended.shown !== step || Math.abs(ended.dx) < turnsAt(ended.stride)) {
        settleBack();
        return;
      }

      // Текущая неделя уходит в сторону пальца, соседняя доезжает на её
      // место. Затем — в одном синхронном куске, до отрисовки — сосед
      // становится текущей неделей, прокрутка встаёт к началу (сосед стоял
      // верхом у верха экрана), и сдвиг снимается начисто: оставшийся
      // `transform` держал бы экран сдвинутым. Глазу подмена не видна —
      // на экране та же неделя в том же месте.
      haptic();
      place(step === 'next' ? -ended.stride : ended.stride, true);
      timers.push(
        setTimeout(() => {
          flushSync(() => {
            latest.current.onTurn(to);
            setPeek(undefined);
          });
          scrollTo({ top: 0, behavior: 'instant' });
          place(0, false);
        }, TURN_MS),
      );
    }

    function onCancel(): void {
      if (gesture?.mode === 'swipe') settleBack();
      gesture = undefined;
    }

    function onClick(event: MouseEvent): void {
      if (Date.now() - quietSince < QUIET_MS) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    area.addEventListener('touchstart', onStart, { passive: true });
    area.addEventListener('touchmove', onMove, { passive: false });
    area.addEventListener('touchend', onEnd);
    area.addEventListener('touchcancel', onCancel);
    area.addEventListener('click', onClick, true);
    return () => {
      for (const timer of timers) clearTimeout(timer);
      area.removeEventListener('touchstart', onStart);
      area.removeEventListener('touchmove', onMove);
      area.removeEventListener('touchend', onEnd);
      area.removeEventListener('touchcancel', onCancel);
      area.removeEventListener('click', onClick, true);
      place(0, false);
      setPeek(undefined);
    };
  }, [options.enabled, shell, page]);

  return { shellRef: setShell, pageRef: setPage, peek };
}
